import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, MessageSquarePlus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_53c9bebf-1df6-461a-ae92-22968598c10c/artifacts/xusxetuj_LOGO.png";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const services = [
    { name: "Drink Machines", path: "/services/drink-machines" },
    { name: "Snack Machines", path: "/services/snack-machines" },
    { name: "Combo Machines", path: "/services/combo-machines" },
    { name: "Micro Markets", path: "/services/micro-markets" },
  ];

  const isActive = (path) => location.pathname === path;
  const isServicesActive = services.some((s) => location.pathname === s.path);

  return (
    <nav className="bg-cream border-b-2 border-navy sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="521 Vending" className="h-16 w-16 object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`font-bold transition-colors hover:text-coral ${
                isActive("/") ? "text-coral" : "text-navy"
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center gap-1 font-bold transition-colors hover:text-coral ${
                  isServicesActive ? "text-coral" : "text-navy"
                }`}
                data-testid="nav-services-dropdown"
              >
                Services <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-2 border-navy rounded-lg shadow-[4px_4px_0px_0px_#1A2A3A]">
                {services.map((service) => (
                  <DropdownMenuItem key={service.path} asChild>
                    <Link
                      to={service.path}
                      className={`font-medium cursor-pointer ${
                        isActive(service.path) ? "text-coral" : "text-navy"
                      }`}
                      data-testid={`nav-${service.name.toLowerCase().replace(" ", "-")}`}
                    >
                      {service.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/how-it-works"
              className={`font-bold transition-colors hover:text-coral ${
                isActive("/how-it-works") ? "text-coral" : "text-navy"
              }`}
              data-testid="nav-how-it-works"
            >
              How It Works
            </Link>

            {/* Machine Suggestions Link */}
            <Link
              to="/suggestions"
              className={`flex items-center gap-1 font-bold transition-colors hover:text-coral ${
                isActive("/suggestions") ? "text-coral" : "text-navy"
              }`}
              data-testid="nav-suggestions"
            >
              <MessageSquarePlus className="h-4 w-4" />
              Suggestions
            </Link>

            <Link
              to="/contact"
              className="btn-primary text-base px-6 py-3"
              data-testid="nav-contact-cta"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-navy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t-2 border-navy" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                className={`font-bold py-2 ${isActive("/") ? "text-coral" : "text-navy"}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-home"
              >
                Home
              </Link>
              
              <div className="font-bold text-navy py-2">Services</div>
              {services.map((service) => (
                <Link
                  key={service.path}
                  to={service.path}
                  className={`font-medium py-2 pl-4 ${
                    isActive(service.path) ? "text-coral" : "text-navy"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${service.name.toLowerCase().replace(" ", "-")}`}
                >
                  {service.name}
                </Link>
              ))}

              <Link
                to="/how-it-works"
                className={`font-bold py-2 ${
                  isActive("/how-it-works") ? "text-coral" : "text-navy"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-how-it-works"
              >
                How It Works
              </Link>

              <Link
                to="/suggestions"
                className={`font-bold py-2 flex items-center gap-2 ${
                  isActive("/suggestions") ? "text-coral" : "text-navy"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-suggestions"
              >
                <MessageSquarePlus className="h-4 w-4" />
                Suggestions
              </Link>

              <Link
                to="/contact"
                className="btn-primary text-center mt-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-contact-cta"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
